import { create } from 'zustand';

/**
 * Tema claro, escuro ou o do sistema operacional. Sem escolha, claro: as telas
 * foram conferidas com o comprador assim, e ninguém abre o sistema escuro de
 * surpresa só porque o Windows está no modo escuro.
 *
 * A escolha fica no navegador com a chave abaixo, lida também pelo script do
 * index.html — é ele que pinta o tema certo antes do React carregar, sem o
 * clarão branco ao abrir o sistema no escuro. Mudou a chave aqui, muda lá.
 */
export type Tema = 'claro' | 'escuro' | 'sistema';

const CHAVE = 'condusvale:tema';

const midia = () => window.matchMedia('(prefers-color-scheme: dark)');

function lido(): Tema {
  try {
    const valor = localStorage.getItem(CHAVE);
    return valor === 'escuro' || valor === 'sistema' ? valor : 'claro';
  } catch {
    return 'claro';
  }
}

function escuro(tema: Tema): boolean {
  return tema === 'escuro' || (tema === 'sistema' && midia().matches);
}

function aplicar(tema: Tema): void {
  document.documentElement.classList.toggle('dark', escuro(tema));
}

interface TemaState {
  tema: Tema;
  /** O que está na tela agora — "sistema" resolvido para claro ou escuro. */
  escuro: boolean;
  escolher: (tema: Tema) => void;
}

export const useTemaStore = create<TemaState>((set) => ({
  tema: lido(),
  escuro: escuro(lido()),
  escolher: (tema) => {
    try {
      localStorage.setItem(CHAVE, tema);
    } catch {
      // Armazenamento bloqueado: o tema vale só até fechar a aba.
    }
    aplicar(tema);
    set({ tema, escuro: escuro(tema) });
  },
}));

// No modo "sistema", acompanha a troca feita no Windows sem recarregar a página.
midia().addEventListener('change', () => {
  const { tema } = useTemaStore.getState();
  if (tema !== 'sistema') return;
  aplicar(tema);
  useTemaStore.setState({ escuro: escuro(tema) });
});
